// EAGER Oracle sniff walker (bundle-isolation: header reads only — the ONLY in-tree import is
// `./sniffWalkers`, no wkx/decoder). Oracle SDO reaches the renderer as a PARSED marker object
// `{ __jamSdoGeometry: { gtype, srid, point, elemInfo, ordinates } }` (T9's adapter change), NOT
// hex — so `spatialSniff.ts`'s `runWalkers` dispatches this BEFORE the hexInfo() path and hands us
// the raw value. Facts are derived from SDO_GTYPE's `dltt` rule (O(1)) plus a cheap point count
// from the ordinate-array LENGTH (never a walk of the array). Returns `container: 'sdo-object'`.
import { dimsLabel, type SniffFacts } from './sniffWalkers';

type SdoWire = {
  gtype: number;
  srid: number | null;
  point: { x: number; y: number; z: number | null } | null;
  elemInfo: number[] | null;
  ordinates: number[] | null;
};

// tt (shape) → GeoJSON type. Index 0 is filler (tt 0 is never valid — see gtype `dltt` rule).
const TT_TO_GEOM = [
  'Unknown', 'Point', 'LineString', 'Polygon', 'GeometryCollection', 'MultiPoint',
  'MultiLineString', 'MultiPolygon',
] as const;

/** Splits an SDO_GTYPE `d l tt` number: d=dimensionality, l=LRS measure position (0=none),
 * tt=shape. `hasM` when an LRS measure dimension is present; `hasZ` when ≥3 non-measure dims. */
export function sdoGtypeParts(gtype: number): {
  dims: number; lrs: number; tt: number; hasZ: boolean; hasM: boolean;
} {
  const dims = Math.floor(gtype / 1000);
  const lrs = Math.floor((gtype % 1000) / 100);
  const tt = gtype % 100;
  const hasM = lrs !== 0;
  const spatialDims = dims - (hasM ? 1 : 0);
  return { dims, lrs, tt, hasZ: spatialDims >= 3, hasM };
}

export function sniffSdoMarker(value: unknown): SniffFacts | null {
  if (!value || typeof value !== 'object' || !('__jamSdoGeometry' in value)) return null;
  const wire = (value as { __jamSdoGeometry: SdoWire }).__jamSdoGeometry;
  if (!wire || typeof wire !== 'object' || typeof wire.gtype !== 'number') return null;

  const { dims, tt, hasZ, hasM } = sdoGtypeParts(wire.gtype);
  const geomType = tt >= 1 && tt <= 7 ? TT_TO_GEOM[tt] : 'Unknown';
  // The adapter serializes a null SDO_ORDINATES to `[]` — an EMPTY array means "no ordinate
  // vertices" (the optimized SDO_POINT form), NOT zero points, so it falls back to 1 like a null.
  const ordinates = Array.isArray(wire.ordinates) && wire.ordinates.length > 0 ? wire.ordinates : null;
  // Cheap point count: ordinate vertices when present. With NO ordinates the 1-point fallback is only
  // valid for an actual optimized point (tt=1) — a non-point gtype (e.g. an empty LineString) whose
  // decode is a parse-error must NOT be reported as "1 pt", so its count is unknown (null).
  const pointCount = ordinates && dims > 0 ? Math.floor(ordinates.length / dims) : tt === 1 ? 1 : null;

  return {
    container: 'sdo-object',
    geomType,
    srid: wire.srid == null || wire.srid === 0 ? null : wire.srid,
    dimensions: dimsLabel(hasZ, hasM),
    pointCount,
    byteLength: (ordinates?.length ?? 0) * 8,
  };
}
